import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import type { RoleAppAccess } from '../components/TeamRoles/roleAppAccessShared';

// Always use the same default app instance so Functions shares auth state with Auth
const fns = () => getFunctions(getApp());

// ── Role management ──────────────────────────────────────────────────────────

export interface CreateRoleRequest {
  name: string;
  description?: string;
  templateKey?: string | null;
  inheritsFrom?: string[];
  grants?: string[];
  /** Round-16 per-role connected-app ceilings (PLAN-connected-apps-governance-20260816 §5). */
  appAccess?: RoleAppAccess | null;
}

export interface UpdateRoleRequest {
  roleId: string;
  name?: string;
  description?: string;
  inheritsFrom?: string[];
  grants?: string[];
  status?: 'active' | 'disabled';
  /** undefined = untouched; null or {} = cleared; object = validated replace. */
  appAccess?: RoleAppAccess | null;
}

export interface PreviewRolePermissionsRequest {
  grants: string[];
  inheritsFrom: string[];
}

export async function createRole(req: CreateRoleRequest) {
  const fn = httpsCallable<CreateRoleRequest, { role: any }>(fns(), 'rbacCreateRole');
  const res = await fn(req);
  return res.data;
}

export async function updateRole(req: UpdateRoleRequest) {
  const fn = httpsCallable<UpdateRoleRequest, { role: any }>(fns(), 'rbacUpdateRole');
  const res = await fn(req);
  return res.data;
}

export async function deleteRole(roleId: string) {
  const fn = httpsCallable<{ roleId: string }, { success: boolean }>(fns(), 'rbacDeleteRole');
  const res = await fn({ roleId });
  return res.data;
}

export async function listRoles() {
  const fn = httpsCallable<Record<string, never>, { roles: any[] }>(fns(), 'rbacListRoles');
  const res = await fn({});
  return res.data;
}

export async function previewRolePermissions(req: PreviewRolePermissionsRequest) {
  const fn = httpsCallable<PreviewRolePermissionsRequest, { effectivePermissions: string[] }>(
    fns(),
    'rbacPreviewRolePermissions'
  );
  const res = await fn(req);
  return res.data;
}

// ── Member management ────────────────────────────────────────────────────────

export interface AssignRolesRequest {
  userId: string;
  roleIds: string[];
}

export async function assignRoles(req: AssignRolesRequest) {
  const fn = httpsCallable<AssignRolesRequest, { member: any }>(fns(), 'rbacAssignRoles');
  const res = await fn(req);
  return res.data;
}

export async function revokeRole(userId: string, roleId: string) {
  const fn = httpsCallable<{ userId: string; roleId: string }, { success: boolean }>(fns(), 'rbacRevokeRole');
  const res = await fn({ userId, roleId });
  return res.data;
}

/** #20: remove a member from the account. Server enforces `team.remove` (separate from invite). */
/** §7.10 pause freeze: suspend a membership (frozen read-only, not removed). */
export async function pauseMember(userId: string) {
  const fn = httpsCallable<{ targetUserId: string }, { status: string; targetUserId: string }>(
    fns(), 'rbacPauseMember',
  );
  const res = await fn({ targetUserId: userId });
  return res.data;
}

/** §7.10 pause freeze: restore a suspended membership to active. */
export async function resumeMember(userId: string) {
  const fn = httpsCallable<{ targetUserId: string }, { status: string; targetUserId: string }>(
    fns(), 'rbacResumeMember',
  );
  const res = await fn({ targetUserId: userId });
  return res.data;
}

export async function removeMember(userId: string) {
  const fn = httpsCallable<{ targetUserId: string }, { status: string; targetUserId: string }>(
    fns(),
    'rbacRemoveMember',
  );
  const res = await fn({ targetUserId: userId });
  return res.data;
}

/**
 * The workspace roster as the SERVER resolves it. `resolvedAccountId` is part of
 * the contract: callers that also read Firestore directly (the thread add-people
 * picker) compare it against their own account so a resolver disagreement is
 * detectable instead of silently rendering as an empty team.
 */
export async function listMembers(accountId?: string) {
  const fn = httpsCallable<{ accountId?: string }, { members: any[]; resolvedAccountId?: string; fullView?: boolean }>(
    fns(),
    'rbacListMembers',
  );
  const res = await fn(accountId ? { accountId } : {});
  return res.data;
}

export async function getMyPermissions(accountId?: string) {
  const fn = httpsCallable<{ accountId?: string }, { permissions: string[]; isOwner: boolean; resolvedAccountId?: string }>(
    fns(),
    'rbacGetMyPermissions'
  );
  // accountId is optional — the Cloud Function resolves it server-side if not provided
  const res = await fn(accountId ? { accountId } : {});
  return res.data;
}

export async function checkPermission(permissionKey: string) {
  const fn = httpsCallable<{ permissionKey: string }, { allowed: boolean }>(fns(), 'rbacCheckPermission');
  const res = await fn({ permissionKey });
  return res.data;
}

export interface DeveloperOrgStatusResult {
  developerOrg: boolean;
  resolvedAccountId?: string;
}

/** Developer-org gate mirror (catalog v4) — decides whether the Developer
 * permission section renders. Cosmetic: every developer endpoint re-checks
 * the gate server-side. */
export async function getDeveloperOrgStatus(accountId?: string): Promise<DeveloperOrgStatusResult> {
  const fn = httpsCallable<{ accountId?: string }, DeveloperOrgStatusResult>(
    fns(),
    'rbacGetDeveloperOrgStatus'
  );
  const res = await fn(accountId ? { accountId } : {});
  return res.data;
}

// ── Invitations ──────────────────────────────────────────────────────────────

export interface CreateInvitationRequest {
  email: string;
  displayName?: string;
  roleIds?: string[];
  memberType?: 'internal' | 'client-portal';
  message?: string;
}

/**
 * What create/resend actually did with the invite email (task #42,
 * 2026-08-10). `emailSent === false` means the invitation EXISTS and the
 * link works — only delivery failed (e.g. email service not configured);
 * `acceptUrl` lets the inviter share the link directly. Both fields are
 * optional so a client deployed ahead of the functions keeps working.
 */
export interface InvitationSendResult {
  inviteId: string;
  token: string;
  status: string;
  emailSent?: boolean;
  emailStatusReason?: string | null;
  acceptUrl?: string;
  orgCreditEligible?: boolean;
  orgCreditReason?: string;
}

export async function createInvitation(req: CreateInvitationRequest): Promise<InvitationSendResult> {
  const fn = httpsCallable<CreateInvitationRequest, InvitationSendResult>(fns(), 'rbacCreateInvitation');
  const res = await fn(req);
  return res.data;
}

/**
 * What acceptance actually did (2026-08-12). `status: 'already-member'` is a
 * SUCCESS: the caller is on the team (they had already accepted, or were a
 * member all along) — never an error card. `becameActive: false` means they
 * kept the workspace they were already using; the UI should say so.
 */
export interface AcceptInvitationResult {
  status: 'accepted' | 'already-member';
  accountId?: string;
  becameActive?: boolean;
  orgCreditGrantedCents?: number;
}

export async function acceptInvitation(token: string, opts?: { accountId?: string; inviteId?: string }): Promise<AcceptInvitationResult> {
  const fn = httpsCallable<{ token: string; accountId?: string; inviteId?: string }, AcceptInvitationResult>(fns(), 'rbacAcceptInvitation');
  const res = await fn({ token, ...(opts ?? {}) });
  return res.data;
}

/**
 * Read-only invite context for the accept page (2026-08-12). Callable while
 * SIGNED OUT — the invite token is the bearer credential — so the landing
 * page can greet the invitee with the real workspace name before they have
 * an account. Privacy: the invited address arrives MASKED (m•••@domain) and
 * the invited-vs-caller email comparison is done server-side
 * (`callerEmailMatchesInvite`: null when signed out). `callerIsMember` /
 * `acceptedByCaller` are only meaningful for signed-in callers.
 */
export interface InvitationPreview {
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  firmName: string;
  invitedEmailMasked: string | null;
  displayName: string | null;
  memberType: 'internal' | 'client-portal';
  roleNames: string[];
  message: string | null;
  invitedByName: string | null;
  expiresAt: number | null;
  accountId: string;
  callerIsMember: boolean;
  acceptedByCaller: boolean;
  callerEmailMatchesInvite: boolean | null;
  /** The INVITED address already has an account (pending invites only) —
   *  lets the needs-auth card lead with sign-in. null/undefined = unknown
   *  (older deployment, lookup failure, consumed invite). */
  invitedEmailHasAccount?: boolean | null;
}

export async function getInvitationPreview(token: string, opts?: { accountId?: string; inviteId?: string }): Promise<InvitationPreview> {
  const fn = httpsCallable<{ token: string; accountId?: string; inviteId?: string }, InvitationPreview>(fns(), 'rbacGetInvitationPreview');
  const res = await fn({ token, ...(opts ?? {}) });
  return res.data;
}

export async function resendInvitation(inviteId: string): Promise<InvitationSendResult> {
  const fn = httpsCallable<{ inviteId: string }, InvitationSendResult>(fns(), 'rbacResendInvitation');
  const res = await fn({ inviteId });
  return res.data;
}

export async function cancelInvitation(inviteId: string) {
  const fn = httpsCallable<{ inviteId: string }, { success: boolean }>(fns(), 'rbacCancelInvitation');
  const res = await fn({ inviteId });
  return res.data;
}

// ── Workspace repair (INCIDENT-20260804) ─────────────────────────────────────

/**
 * A firm whose RBAC layer (members/roles/invitations) sits in a different account
 * document from its actual workspace (threads, history, memory, VM routing).
 * Team & Roles then lists a team that the thread write path cannot see, and
 * adding a colleague to a thread fails `not_admittable_fail_closed`.
 */
export interface WorkspaceConsolidationPlan {
  split: boolean;
  sourceAccountId: string | null;
  targetAccountId: string | null;
  summary: string;
  roleIdsToCopy: string[];
  memberIdsToCopy: string[];
  inviteIdsToCopy: string[];
  memberIdsAlreadyPresent: string[];
  blockers: string[];
  sessionAccountId?: string | null;
  rbacAccountId?: string | null;
  actionable?: boolean;
}

/** Read-only. Safe to call on every Team & Roles load. */
export async function diagnoseWorkspace() {
  const fn = httpsCallable<Record<string, never>, WorkspaceConsolidationPlan>(
    fns(),
    'rbacDiagnoseWorkspace',
  );
  const res = await fn({});
  return res.data;
}

/**
 * Copy the RBAC layer into the workspace account. `apply:false` (the default)
 * returns the exact ledger without writing anything — always preview first.
 * Create-only: an existing document in the target is never overwritten, and the
 * source account is never modified or deleted.
 */
export async function consolidateWorkspace(opts?: { apply?: boolean }) {
  const fn = httpsCallable<
    { apply?: boolean },
    {
      status: 'no_split' | 'blocked' | 'dry_run' | 'applied';
      applied: boolean;
      plan: WorkspaceConsolidationPlan;
      ledger: Array<{ path: string; action: 'copy' | 'skip-exists' | 'skip-missing' }>;
      backPointers?: { repointed: string[]; leftPointingElsewhere: string[] };
    }
  >(fns(), 'rbacConsolidateWorkspace');
  const res = await fn({ apply: opts?.apply === true });
  return res.data;
}

// ── Workspace switching ──────────────────────────────────────────────────────

/**
 * One workspace the caller may act in, as the SERVER verified it — every
 * entry passed verifyWorkspaceAccess (a forged back-pointer contributes
 * nothing). `via` is the verification path that admitted it
 * ('active_member' | 'account_owner' | 'self_account').
 */
export interface WorkspaceOption {
  accountId: string;
  displayName: string;
  isActive: boolean;
  isOwner: boolean;
  via: string;
  /** Display role from the authoritative member doc ('owner', 'admin', …) —
   *  2026-08-18: labels no longer guessed from the isOwner boolean alone. */
  roleLabel?: string;
  /** The caller's own personal workspace, not yet set up (lazy model,
   *  2026-08-19): the first click ACTIVATES it (trial + onboarding + VM)
   *  instead of switching into it. */
  dormant?: true;
  /** The caller's own personal workspace, active and reversible: it can be
   *  put back to "set up" without deleting anything
   *  (deactivatePersonalWorkspace). Server-computed — it needs the lazy flag
   *  AND the posture, and a control that might be refused is worse than
   *  none. */
  canSetAside?: true;
}

/**
 * Idempotent ensure of the caller's account-native organization (+ once-ever
 * $100 starter grant, deduped server-side by canonical identity) — multi-org
 * plan D2, 2026-08-16. Safe to fire opportunistically; errors are the
 * caller's choice to ignore (an older functions deployment must not break
 * any surface that calls this).
 */
export async function ensureNativeOrg(): Promise<{
  accountId: string;
  profileEnsured: boolean;
  starterGranted: boolean;
  starterReason?: string;
}> {
  const fn = httpsCallable<
    Record<string, never>,
    { accountId: string; profileEnsured: boolean; starterGranted: boolean; starterReason?: string }
  >(fns(), 'ensureNativeOrg');
  const res = await fn({});
  return res.data;
}

/**
 * First-click activation of the caller's dormant personal workspace (lazy
 * model, 2026-08-19): mints structure + the uniform trial on the uid plane
 * and stamps nativePosture 'active'. No VM inside the callable — the warm
 * path boots it while the user onboards. Idempotent; safe to retry.
 */
export async function activatePersonalWorkspace(): Promise<{
  accountId: string;
  activated: boolean;
  trialStarted: boolean;
  trialReason?: string;
}> {
  const fn = httpsCallable<
    Record<string, never>,
    { accountId: string; activated: boolean; trialStarted: boolean; trialReason?: string }
  >(fns(), 'activatePersonalWorkspace');
  const res = await fn({});
  return res.data;
}

/**
 * The way back out of the personal workspace (owner ask 2026-09-09: choosing
 * it "should take the user to a new onboarding … and easily be able to go
 * back"). Nothing is deleted — the structure, the trial and any work stay put
 * and re-activating restores them; only the posture returns to dormant, which
 * is what makes the switcher render "set up" again.
 *
 * `deactivated:false` with `reason:'eager-mode'` means the fleet is not in
 * lazy mode, where the platform would re-activate the workspace on the next
 * page mount and there is no chooser to return to — surface it, do not retry.
 */
export async function deactivatePersonalWorkspace(): Promise<{
  accountId: string;
  deactivated: boolean;
  reason?: 'already-dormant' | 'eager-mode';
}> {
  const fn = httpsCallable<
    Record<string, never>,
    { accountId: string; deactivated: boolean; reason?: 'already-dormant' | 'eager-mode' }
  >(fns(), 'deactivatePersonalWorkspace');
  const res = await fn({});
  return res.data;
}

/**
 * Voluntary organization exit (owner directive 2026-08-16): non-owners only.
 * The server converts every org thread to the owners (full history) BEFORE
 * membership goes away, then flips the back-pointer home if this was the
 * active workspace. Throws HttpsError on ineligibility (is-owner /
 * native-org / not-a-member) — callers surface e.message.
 */
export async function leaveOrganization(accountId: string) {
  const fn = httpsCallable<
    { accountId: string },
    { status: 'left'; threadsConverted: number; threadsSeen: number }
  >(fns(), 'leaveOrganization');
  const res = await fn({ accountId });
  return res.data;
}

/** Read-only: the workspaces this caller may switch to, with the active one marked. */
export async function listMyWorkspaces() {
  const fn = httpsCallable<
    Record<string, never>,
    { activeAccountId: string; workspaces: WorkspaceOption[] }
  >(fns(), 'listMyWorkspaces');
  const res = await fn({});
  return res.data;
}

/**
 * Point the caller's active workspace at one they demonstrably belong to
 * (membership is re-verified server-side; switching away never removes a
 * membership). Every downstream consumer re-derives from the
 * account_membership/main write — callers should still do a clean
 * navigation afterwards to flush per-module accountId caches.
 */
export async function switchActiveWorkspace(accountId: string) {
  const fn = httpsCallable<
    { accountId: string },
    { ok: boolean; activeAccountId: string; workspaces: WorkspaceOption[] }
  >(fns(), 'switchActiveWorkspace');
  const res = await fn({ accountId });
  return res.data;
}

/**
 * PER-ORG WALLET (2026-08-19): owner-only personal→organization pool
 * funding. Server-side verification (owner + workspace-mode) lives in
 * functions/src/wallet/orgWalletTransfer.ts; `transferId` makes retries
 * idempotent — callers pass a fresh random id per user action.
 */
export async function transferToOrgWallet(accountId: string, amountOlv: number, transferId: string) {
  const fn = httpsCallable<
    { accountId: string; amountOlv: number; transferId: string },
    { ok: boolean; moved: boolean; accountId: string; amountOlv: number }
  >(fns(), 'transferToOrgWallet');
  const res = await fn({ accountId, amountOlv, transferId });
  return res.data;
}

// ── Admin / Maintenance ──────────────────────────────────────────────────────

export async function recomputeAccount() {
  const fn = httpsCallable<Record<string, never>, { success: boolean }>(fns(), 'rbacRecomputeAccount');
  const res = await fn({});
  return res.data;
}

export async function seedDefaultRoles() {
  const fn = httpsCallable<Record<string, never>, { success: boolean }>(fns(), 'rbacSeedDefaultRoles');
  const res = await fn({});
  return res.data;
}

// PRE-163: Super-admin only — scans all accounts for stale RBAC state and backfills
export async function scanAndBackfillAllAccounts(limit = 50) {
  const fn = httpsCallable<{ limit: number }, { status: string; processed: number; results: any[] }>(
    fns(),
    'rbacScanAndBackfillAllAccounts'
  );
  const res = await fn({ limit });
  return res.data;
}

// ── Invitations listing (ORG-USAGE-2026) ─────────────────────────────────────

export interface InvitationSummary {
  inviteId: string;
  email: string;
  displayName: string | null;
  memberType: 'internal' | 'client-portal';
  roleIds: string[];
  status: 'pending' | 'accepted' | 'expired' | 'cancelled';
  invitedBy: string;
  message: string | null;
  createdAt: number | null;
  expiresAt: number | null;
  acceptedBy: string | null;
  acceptedAt: number | null;
}

export async function listInvitations(opts?: { accountId?: string; includeResolved?: boolean }) {
  const fn = httpsCallable<
    { accountId?: string; includeResolved?: boolean },
    { invitations: InvitationSummary[]; resolvedAccountId: string }
  >(fns(), 'rbacListInvitations');
  const res = await fn(opts ?? {});
  return res.data;
}

// ── Usage limits (ORG-USAGE-2026) ────────────────────────────────────────────

export type UsageLimitWindowKind =
  | 'calendar-day'
  | 'calendar-week'
  | 'calendar-month'
  | 'rolling';

export interface UsageLimitWindow {
  id: string;
  kind: UsageLimitWindowKind;
  amount?: number;
  unit?: 'hours' | 'days';
  /** token cap — optional since $-based limits (2026-08-03); ≥1 cap required */
  tokenLimit?: number;
  /** USD spend cap (user-facing cost) — the $-first way admins set limits */
  costUsdLimit?: number;
  label?: string;
}

export interface UsageLimitsConfig {
  windows: UsageLimitWindow[];
}

export interface RoleLimitSummary {
  roleId: string;
  name: string;
  system: boolean;
  status: string;
  assignedMemberCount: number;
  usageLimits: UsageLimitsConfig | null;
}

export interface MemberLimitSummary {
  userId: string;
  email: string | null;
  displayName: string | null;
  status: string;
  isOwner: boolean;
  assignedRoleIds: string[];
  usageLimitOverride: UsageLimitsConfig | null;
  effectiveUsageLimits: { windows: UsageLimitWindow[]; source: 'override' | 'roles' } | null;
}

export async function getUsageLimits(accountId?: string) {
  const fn = httpsCallable<
    { accountId?: string },
    {
      roles: RoleLimitSummary[];
      members: MemberLimitSummary[];
      enforcement: { enforced: boolean; reason: string | null };
      resolvedAccountId: string;
    }
  >(fns(), 'rbacGetUsageLimits');
  const res = await fn({ accountId });
  return res.data;
}

export interface MemberLimitWindowStatus {
  label: string;
  /** binding metric — used/limit are tokens or user-facing USD */
  metric: 'tokens' | 'usd';
  used: number;
  limit: number;
  ratio: number;
  level: 'none' | 'approaching' | 'critical' | 'reached';
  resetMs: number;
  exceeded: boolean;
}

export interface MemberLimitStatus {
  userId: string;
  email: string | null;
  displayName: string | null;
  source: string;
  windows: MemberLimitWindowStatus[];
}

/** Live usage-vs-limit status for every member with limits (admin overview). */
export async function getMemberLimitStatuses(accountId?: string) {
  const fn = httpsCallable<
    { accountId?: string },
    {
      statuses: MemberLimitStatus[];
      truncated: boolean;
      totalWithLimits: number;
      resolvedAccountId: string;
    }
  >(fns(), 'rbacGetMemberLimitStatuses');
  const res = await fn({ accountId });
  return res.data;
}

export async function setRoleUsageLimits(
  roleId: string,
  usageLimits: UsageLimitsConfig | null,
  accountId?: string
) {
  const fn = httpsCallable<
    { roleId: string; usageLimits: UsageLimitsConfig | null; accountId?: string },
    { roleId: string; status: string; usageLimits: UsageLimitsConfig | null }
  >(fns(), 'rbacSetRoleUsageLimits');
  const res = await fn({ roleId, usageLimits, accountId });
  return res.data;
}

export async function setMemberUsageLimits(
  targetUserId: string,
  usageLimits: UsageLimitsConfig | null,
  accountId?: string
) {
  const fn = httpsCallable<
    { targetUserId: string; usageLimits: UsageLimitsConfig | null; accountId?: string },
    { targetUserId: string; status: string; effectiveUsageLimits: any }
  >(fns(), 'rbacSetMemberUsageLimits');
  const res = await fn({ targetUserId, usageLimits, accountId });
  return res.data;
}

/**
 * Idempotent grandfather migration. Safe to call repeatedly — returns
 * `already_upgraded` once an account is on the current generation.
 */
export async function upgradeUsageVisibility(accountId?: string) {
  const fn = httpsCallable<
    { accountId?: string },
    { status: string; accountId: string; generation: number; rolesUpdated?: number; membersStamped?: number }
  >(fns(), 'rbacUpgradeUsageVisibility');
  const res = await fn({ accountId });
  return res.data;
}

// ── Activity (ORG-USAGE-2026) ────────────────────────────────────────────────

/**
 * R18 (2026-08-09): one entry per MODEL CALL of the request, in call order —
 * the same per-call series the chat ⓘ popup renders, so the two surfaces are
 * directly comparable. Raw model ids; the UI resolves codenames. Amounts are
 * the user-facing (marked-up) figures.
 */
export interface ActivityEventGeneration {
  model: string;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  amountUsd: number;
  amountOlv: number;
  /** Request-side reasoning-effort level (none…max) when the call site stamped it. */
  effort?: string;
  /** Background-work stage label (thread-ledger PR-5): 'ledger-compaction' |
   *  'memory-extraction' | 'media-render' | 'read-aloud' | 'computer-run' — the server whitelist admits only the closed set. */
  stage?: string;
  /** 2026-09-08: 'estimate' when a media render was priced from the SSOT unit price (rendered
   *  with a "≈" marker), 'provider' when the vendor reported the cost. */
  costSource?: 'provider' | 'estimate';
}

export interface ActivityEvent {
  eventId: string;
  memberId: string | null;
  /** P3: what the work WAS. Null on every row written before 2026-09-16 — read
   *  it through `source` there (isBackgroundExpenseSource). */
  kind?: string | null;
  timestampMs: number | null;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  /** List: the provider's cost × 5. */
  amountUsd: number;
  amountOlv: number;
  /**
   * What the person paid for this line, as the debit stamped it (2026-10-17, billing/usagePrice.ts): the plan's part
   * at what the plan paid per pr0, the Wallet's at list. Null on a line recorded before the stamp (it paid list).
   */
  youPayUsd?: number | null;
  /** What the plan's rate saved against list, and a part nothing could pay (never billed). */
  savingUsd?: number;
  overrunUsd?: number;
  modelId: string | null;
  provider: string | null;
  source: string | null;
  sessionId: string | null;
  /** The thread the line's session belongs to, so the line links to its conversation (2026-10-18). Absent from an older server. */
  threadId?: string | null;
  summary: string | null;
  /** The task this line belongs to when it is someone else's work on it, or another day's (2026-10-17). */
  parentEventId?: string | null;
  /**
   * What the line went to (2026-10-18): the kinds "by kind of work" splits it into, its own first (slugs of
   * config/workKindVocabulary.ts), and whether a message's ⓘ shows it. The server decides both; absent from an older one.
   */
  work?: string[] | null;
  onMessage?: boolean | null;
  /** wall-clock turn duration; null for events recorded before round 2 */
  durationMs: number | null;
  /** R20: background time (memory update, follow-ups) folded onto this task's
      row — tracked separately, never added into durationMs. */
  auxDurationMs?: number | null;
  testMode: boolean;
  /** Per-call breakdown; empty for events recorded before R18. */
  generations?: ActivityEventGeneration[];
  /**
   * The ask this row is (2026-10-24, one ask, one total): the server lists one row per ask, person and day
   * (functions/src/rbac/activityAskRows.ts) — how many lines it is, its parts, and its steps and apps as its ⓘ counts
   * them. Absent on a line that names no ask, and from an older server. Read it through askOfEvent.
   */
  ask?: ActivityEventAsk | null;
}

/** What a row says of its ask (functions/src/rbac/activityAskRows.ts ActivityAsk). */
export interface ActivityEventAsk {
  taskId: string;
  lines: number;
  parts: Array<{ part: string; lines: number; amountUsd: number; youPayUsd: number }>;
  steps: number | null;
  apps: number | null;
}

/** Server-resolved visibility of an activity response (R19 adds 'subtree'). */
export type ActivityScopeKind = 'own' | 'subtree' | 'team';

export async function listActivityEvents(params: {
  accountId?: string;
  memberId?: string;
  /** scope to a role and everything beneath it (R19); server-validated */
  roleId?: string;
  startMs?: number;
  endMs?: number;
  source?: string;
  /** raw model id (server-side equality filter; UI shows the codename) */
  modelId?: string;
  /**
   * All raw ids behind one selected codename (the map is many-to-one) — the
   * server filters across every one of them. Wins over modelId when set.
   */
  modelIds?: string[];
  /**
   * P3 (2026-09-16): filter by WHAT the work was — 'user-turn' | 'routine' |
   * 'platform-summon' | 'sweep' | 'decision' | 'coding-run' | 'note'. The
   * shorthand '!user-turn' means everything Prest0n did on his own, so the
   * page never has to enumerate a list the server owns. Rows written before
   * P3 carry no kind and are classified by their source, so history filters
   * correctly instead of disappearing. '!on-a-message' (2026-10-18) means the
   * lines no message's ⓘ shows (config/workKindVocabulary.ts).
   */
  kinds?: string[];
  pageSize?: number;
  cursorMs?: number;
  /** Compound-cursor tiebreak — the document id paired with cursorMs. */
  cursorId?: string;
}) {
  const fn = httpsCallable<
    typeof params & { scopeAware: true },
    {
      events: ActivityEvent[];
      hasMore: boolean;
      nextCursorMs: number | null;
      nextCursorId?: string | null;
      scope: ActivityScopeKind;
      resolvedAccountId: string;
    }
  >(fns(), 'activityListEvents');
  // scopeAware: this bundle understands scope 'subtree' (R19). Without the
  // marker the server keeps subtree-permission holders on own-scope so
  // CDN-cached pre-R19 bundles never render blended, unattributed team rows.
  const res = await fn({ ...params, scopeAware: true });
  return res.data;
}

export interface ActivityRequestStats {
  requests: number;
  avgTokensPerRequest: number;
  p95TokensPerRequest: number;
  avgCostUsdPerRequest: number;
  p95CostUsdPerRequest: number;
  avgDurationMs: number | null;
  p95DurationMs: number | null;
  avgTokensPerSecond: number | null;
}

export interface ActivityDailyByModel {
  /** raw model ids ranked by tokens; '__other__' collapses the tail */
  models: string[];
  series: Array<{
    dayKey: string;
    tokens: Record<string, number>;
    usd: Record<string, number>;
  }>;
}

/** Chart granularity (R19.5). Server coarsens when the range is too wide. */
export type ActivityBucket =
  | '1m' | '5m' | '15m' | '30m'
  | '1h' | '3h' | '6h' | '12h'
  | '1d' | '2d' | '1w' | '1mo';

export interface ActivityBucketedSeries {
  bucket: ActivityBucket;
  /** true when a sub-day series rode a truncated event scan */
  approximate: boolean;
  series: Array<{
    startMs: number;
    usd: number;
    tokens: number;
    turns: number;
    /**
     * In-scope TEST-mode turns in this bucket (2026-08-17): recorded in the
     * events list but never billed, so they ride a separate lane — the
     * timeline can reflect them without polluting the billable series.
     * Absent from pre-rollout responses.
     */
    testTurns?: number;
  }>;
  byModel: {
    /** raw model ids ranked by usd; the client resolves station names */
    models: string[];
    series: Array<{ startMs: number; usd: Record<string, number> }>;
  };
}

/** One kind of work's sums in a window (activitySummary's byWork). */
export interface ActivityWorkTotal {
  /** A slug of config/workKindVocabulary.ts WORK_KINDS. */
  work: string;
  usd: number;
  paidUsd?: number;
  count: number;
}

export interface ActivitySummaryResponse {
  scope: ActivityScopeKind;
  days: number;
  /** the exact window the numbers cover (custom calendar ranges included) */
  rangeStartMs?: number;
  rangeEndMs?: number;
  /** the chart series at the requested (possibly coarsened) granularity */
  bucketed?: ActivityBucketedSeries;
  /** true when the usage read failed — totals are incomplete, not zero */
  partial?: boolean;
  /** per-day per-raw-model stack (zero-filled day domain) */
  dailyByModel?: ActivityDailyByModel;
  /** request-level stats over the bounded event scan */
  stats?: ActivityRequestStats;
  statsTruncated?: boolean;
  /** `usd` is list; `paidUsd` what was paid (2026-10-18) — absent from a server that predates it. */
  totals: { tokens: number; usd: number; paidUsd?: number; turns: number; members: number };
  dailySeries: Array<{ dayKey: string; tokens: number; usd: number; turns: number }>;
  byMember: Array<{
    memberId: string;
    tokens: number;
    usd: number;
    /** What this person's work was paid (2026-10-18): list less the plan's saving and anything not billed. */
    paidUsd?: number;
    turns: number;
    email?: string | null;
    displayName?: string | null;
  }>;
  byModel: Array<{ modelId: string; tokens: number; usd: number; count: number }>;
  bySource: Array<{ source: string; tokens: number; usd: number; count: number }>;
  /**
   * By kind of work (2026-10-18, config/workKindVocabulary.ts): each line split by its parts. `usd` is list, `paidUsd`
   * what was paid for it, `count` the lines it appears on. Absent from an older server.
   */
  byWork?: ActivityWorkTotal[];
  /** R21 — test-mode usage in the window (recorded, never billed): keeps a
      test-only workspace's page fully populated, clearly labeled. */
  testUsage?: {
    turns: number;
    usd: number;
    olv: number;
    byMember: Array<{
      memberId: string;
      turns: number;
      usd: number;
      olv: number;
      email?: string | null;
      displayName?: string | null;
    }>;
    byModel: Array<{ modelId: string; turns: number; usd: number; olv: number }>;
    byWork?: ActivityWorkTotal[];
  };
  /**
   * Compute time (usage-metering step 7, 2026-10-19): how long the window's work held a workstation, the phone line or
   * the media service — included, never charged. Whole UTC days; by member beyond one's own rows. null when the server
   * could not read it; absent from an older server.
   */
  computeTime?: ActivityComputeTime | null;
  resolvedAccountId: string;
}

/** activitySummary's computeTime: milliseconds of each kind, in all and by member. */
export interface ActivityComputeTime {
  totals: { workstationMs: number; phoneMs: number; mediaMs: number };
  byMember: Array<{
    memberId: string;
    workstationMs: number;
    phoneMs: number;
    mediaMs: number;
    email?: string | null;
    displayName?: string | null;
  }>;
  /** The window held more days than one read takes: the figures are its most recent days. */
  recentOnly?: boolean;
}

export async function getActivitySummary(params: {
  accountId?: string;
  days?: number;
  /** custom calendar window (wins over days when both bounds are set) */
  startMs?: number;
  endMs?: number;
  /** chart granularity; server coarsens when the range is too wide */
  bucket?: ActivityBucket;
  memberId?: string;
  /** scope to a role and everything beneath it (R19); server-validated */
  roleId?: string;
}) {
  const fn = httpsCallable<typeof params & { scopeAware: true }, ActivitySummaryResponse>(
    fns(),
    'activitySummary'
  );
  // scopeAware: see listActivityEvents — pre-R19 bundles stay own-scoped.
  const res = await fn({ ...params, scopeAware: true });
  return res.data;
}

// ── Activity scope options (R19 role-scoped visibility) ─────────────────────

export interface ActivityScopeRole {
  roleId: string;
  name: string;
  /** parents WITHIN the returned set — roots have none; client builds the tree */
  parentRoleIds: string[];
  /** members holding this role or any role beneath it (within visibility) */
  memberCount: number;
  hasLimits: boolean;
}

export interface ActivityScopeMember {
  memberId: string;
  displayName: string | null;
  email: string | null;
  isOwner: boolean;
  primaryRoleId: string | null;
  roleIds: string[];
}

export interface ActivityScopeOptionsResponse {
  mode: ActivityScopeKind;
  roles: ActivityScopeRole[];
  members: ActivityScopeMember[];
  viewerRoleIds: string[];
  resolvedAccountId: string;
}

/**
 * The role tree + members the viewer may scope the Activity page to. Team
 * viewers get the whole workspace; subtree viewers exactly their teams; the
 * server re-validates every selection on the data endpoints regardless.
 */
export async function getActivityScopeOptions(accountId?: string) {
  const fn = httpsCallable<{ accountId?: string }, ActivityScopeOptionsResponse>(
    fns(),
    'activityScopeOptions'
  );
  const res = await fn(accountId ? { accountId } : {});
  return res.data;
}

export interface MyUsageLimit {
  id: string;
  kind: UsageLimitWindowKind;
  amount: number | null;
  unit: 'hours' | 'days' | null;
  label: string;
  description: string;
  /** the binding metric `used`/`limit` are measured in ($ caps vs token caps) */
  metric: 'tokens' | 'usd';
  used: number;
  limit: number;
  usedTokens: number;
  usedUsd: number;
  ratio: number;
  level: 'none' | 'approaching' | 'critical' | 'reached';
  resetMs: number;
  exceeded: boolean;
}

export async function getMyUsage(accountId?: string) {
  const fn = httpsCallable<
    { accountId?: string },
    {
      limits: MyUsageLimit[];
      hasLimits: boolean;
      isOwner: boolean;
      /** false when the workspace plan/billing config means limits aren't applied */
      enforced?: boolean;
      source?: string;
      resolvedAccountId: string;
    }
  >(fns(), 'activityMyUsage');
  const res = await fn({ accountId });
  return res.data;
}

// ── Cross-org activity viewer (R23, Prest0 Inc. exception) ──────────────────

export interface ActivityOrganizationRow {
  accountId: string;
  displayName: string;
}

/**
 * The org lookup behind the Prest0 Inc. cross-org activity viewer. Server-side
 * gate: owner/admin of the enabled developer org — for everyone else this
 * throws permission-denied, which is also how the page decides whether to
 * render the button at all (the server is the only role oracle; the client
 * guesses nothing).
 */
export async function listActivityOrganizations() {
  const fn = httpsCallable<Record<string, never>, { organizations: ActivityOrganizationRow[] }>(
    fns(),
    'activityListOrganizations'
  );
  const res = await fn({});
  return res.data;
}

// ── Organization spending limit + balance stop (2026-08-14) ─────────────────

export type PoolWindowUnit = 'minutes' | 'hours' | 'days' | 'weeks';
export type PoolWindowKind = 'rolling' | 'calendar-day' | 'calendar-week' | 'calendar-month';

export interface PoolWindow {
  id: string;
  kind: PoolWindowKind;
  amount?: number;
  unit?: PoolWindowUnit;
  costUsdLimit: number;
  label?: string;
}

export interface AccountSpendLimits {
  windows: PoolWindow[];
  balanceStop: { enabled: boolean; floorUsd: number };
}

export interface PoolWindowStatus {
  id: string;
  label: string;
  kind: PoolWindowKind;
  usedUsd: number;
  limitUsd: number;
  ratio: number;
  resetMs: number;
}

export interface AccountSpendLimitsStatus {
  accountId: string;
  config: AccountSpendLimits;
  balanceUsd: number | null;
  /** Live per-window spend — one entry per configured limit. */
  windowsStatus: PoolWindowStatus[];
  canManage: boolean;
}

/** Read the account-wide spending guardrails and live spend against them. */
export async function getAccountSpendLimits(): Promise<AccountSpendLimitsStatus> {
  const fn = httpsCallable<Record<string, never>, AccountSpendLimitsStatus>(
    fns(),
    'rbacGetAccountSpendLimits',
  );
  const res = await fn({});
  return res.data;
}

/** Replace the account-wide spending guardrails (permission-gated server-side). */
export async function setAccountSpendLimits(input: {
  windows: PoolWindow[];
}): Promise<{ success: boolean; config: AccountSpendLimits }> {
  const fn = httpsCallable<typeof input, { success: boolean; accountId: string; config: AccountSpendLimits }>(
    fns(),
    'rbacSetAccountSpendLimits',
  );
  const res = await fn(input);
  return res.data;
}
